import { useState } from 'react';
import { Button } from '@/components/atoms/button';
import { NewEstimatePaymentScheduleDialog } from '@/components/organisms/estimates/NewEstimatePaymentScheduleDialog';
import {
  validateDraftEstimatePaymentSchedule,
  type DraftEstimatePaymentSchedule,
} from '@/components/organisms/payment-schedules/estimateTemplateValidation';

interface ProposalOptionPaymentScheduleProps {
  value: DraftEstimatePaymentSchedule | null;
  contactId: string | null;
  grandTotal: number;
  depositRequired: boolean;
  depositAmount: number;
  allowPaymentSchedules: boolean;
  requireScheduleAboveAmount: number;
  onChange: (value: DraftEstimatePaymentSchedule | null) => void;
}

export function ProposalOptionPaymentSchedule({
  value,
  contactId,
  grandTotal,
  depositRequired,
  depositAmount,
  allowPaymentSchedules,
  requireScheduleAboveAmount,
  onChange,
}: ProposalOptionPaymentScheduleProps) {
  const [open, setOpen] = useState(false);
  const required = requireScheduleAboveAmount > 0 && grandTotal > requireScheduleAboveAmount;
  const errors = value
    ? validateDraftEstimatePaymentSchedule(value, grandTotal, {
        required: depositRequired,
        amount: depositAmount,
      })
    : [];

  if (!allowPaymentSchedules && !value && !required) return null;

  return (
    <div className="space-y-2 rounded-md border p-3">
      <div className="flex items-center justify-between gap-3">
        <div>
          <p className="text-sm font-medium">Payment schedule</p>
          <p className="text-xs text-muted-foreground">
            {value
              ? value.previewText ||
                `${value.rules.length} payment stage${value.rules.length === 1 ? '' : 's'} configured`
              : 'Choose payment stages for this option.'}
          </p>
        </div>
        <Button
          type="button"
          variant="outline"
          size="sm"
          disabled={!allowPaymentSchedules}
          onClick={() => setOpen(true)}
        >
          {value ? 'Edit payment schedule' : 'Set up payment schedule'}
        </Button>
      </div>
      {value ? (
        <p className="text-xs text-muted-foreground">
          The customer reviews this schedule at checkout before accepting.
        </p>
      ) : null}
      {required && !value ? (
        <p role="alert" className="text-xs text-destructive">
          This option requires a payment schedule before it can be sent. Set up its payment schedule
          to continue.
        </p>
      ) : null}
      {!allowPaymentSchedules ? (
        <p role="alert" className="text-xs text-muted-foreground">
          Payment schedules are disabled in estimate settings.
        </p>
      ) : null}
      {!allowPaymentSchedules && value ? (
        <Button type="button" variant="outline" size="sm" onClick={() => onChange(null)}>
          Remove payment schedule
        </Button>
      ) : null}
      {errors.length > 0 ? (
        <ul role="alert" className="list-disc pl-4 text-xs text-destructive">
          {errors.map((error) => (
            <li key={error}>{error}</li>
          ))}
        </ul>
      ) : null}
      <NewEstimatePaymentScheduleDialog
        open={open}
        onOpenChange={setOpen}
        value={value}
        contactId={contactId}
        grandTotal={grandTotal}
        depositRequired={depositRequired}
        depositAmount={depositAmount}
        description="Choose the stages for this proposal option. The customer will review and accept the schedule at checkout. Changes are saved with the proposal."
        useDefaultPreset={false}
        onSave={onChange}
        onRemove={() => onChange(null)}
      />
    </div>
  );
}
